import { IChatServerService } from '@/app-modules/server-service';
import { RoomUserModel, UserListResponse, AddUserResponse, RemoveUserResponse, UserResponse } from '@/components/chat/_domain';
import { IUserChatStatusService, UserChatStatus } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { container } from '@/app-modules/_ioc';
import { ModifyUserMessageResponse } from '@/components/chat/_domain/server';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IConfigurationService, ConfigurationPaths } from '@/app-modules/config';
import { IServerCommand } from '@/app-modules/server-communications';
import { ISubscription } from 'rxjs/Subscription';
import { Subject } from 'rxjs/Subject';

const USER_LIST_CHUNK_SIZE = 150;
const USER_LIST_INITIALISATION_DELAY_MILLISECONDS = 200;

class RoomUserContainer {
  public userList: RoomUserModel[] = [];
  private backedUserList: Set<string> = new Set();
  private userChatStatusService: IUserChatStatusService;
  private readonly userListChangeSubject = new Subject<void>();

  constructor(
    private readonly configurationService: IConfigurationService,
    private server: IChatServerService,
    private gameName: string,
    private chatRoomName: string) {
    this.userChatStatusService = container.get(SERVICE_IDENTIFIERS.IUserChatStatusService);
  }

  public userListCount(): number {
    return this.userList ? this.userList.length : 0;
  }

  public subscribeUserListChanged(func: () => void): ISubscription {
    return this.userListChangeSubject.subscribe(func);
  }

  private notifyUserListChanged(): void {
    this.userListChangeSubject.next(void 0);
  }

  public getUserListStreamSubscriptions(): ISubscription[] {
    return [
      this.server.subscribeUserList((payload: UserListResponse) => this.initialiseUserList(payload)),
      this.server.subscribeAddUser((payload: AddUserResponse) => this.addUser(payload)),
      this.server.subscribeRemoveUser((payload: RemoveUserResponse) => this.removeUser(payload)),
      this.server.subscribeModifyUserMessage((payload: ModifyUserMessageResponse) => this.modifyUser(payload))
    ];
  }

  private sortUsersAlphabeticallyByUsername(user1: RoomUserModel, user2: RoomUserModel, isAlphabetical: boolean) {
    return (isAlphabetical ? user1.username.localeCompare(user2.username) : user2.username.localeCompare(user1.username));
  }

  private isUsersBirthdayAndIsEitherANewOrReturningUser(user1: RoomUserModel, user2: RoomUserModel): boolean {
    return (user2.birthday && user1.birthday) && ((user2.returningPlayer || user1.returningPlayer) || (user2.newMember || user1.newMember));
  }

  public async sortByUserTypeThenAlphabeticallyByUsername(userListSortAscending: boolean): Promise<void> {
    let cmUsersAsList: RoomUserModel[] = [];
    let tempUserList: RoomUserModel[] = [];
    tempUserList.push(...this.userList);
    this.userList = [];
    const birthdaysEnabled = await this.isBirthdaysEnabled();
    cmUsersAsList = tempUserList.filter((user) => {
      return user.userChatStatus === UserChatStatus.ChatModerator || user.userChatStatus === UserChatStatus.TeamLeader || user.userChatStatus === UserChatStatus.ShiftLeader;
    }).sort();

    tempUserList = tempUserList.filter((user) => {
      return user.userChatStatus !== UserChatStatus.ChatModerator && user.userChatStatus !== UserChatStatus.TeamLeader && user.userChatStatus !== UserChatStatus.ShiftLeader;
    }).sort((user1, user2) => {
      if (this.isUsersBirthdayAndIsEitherANewOrReturningUser(user1, user2) && birthdaysEnabled) {
        return this.sortUsersAlphabeticallyByUsername(user1, user2, userListSortAscending);
      }
      return (birthdaysEnabled &&  +user2.birthday - +user1.birthday) ||
        +user2.returningPlayer - +user1.returningPlayer ||
        +user2.newMember - +user1.newMember ||
        this.sortUsersAlphabeticallyByUsername(user1, user2, userListSortAscending);
    });

    // Add CMs back into the list - so they are always top of the user list
    cmUsersAsList.forEach((cmUser) => {
      tempUserList.unshift(cmUser);
    });

    while (tempUserList.length > 0) {
      await new Promise((resolve) => {
        const userListChunk = tempUserList.splice(0, USER_LIST_CHUNK_SIZE);

        window.setTimeout(() => {
          this.userList.push(...userListChunk);
          this.notifyUserListChanged();
          resolve();
        }, USER_LIST_INITIALISATION_DELAY_MILLISECONDS);
      });
    }
  }

  public findRoomUser(username: string): RoomUserModel {
    return this.userList.find((user) => user.username === username);
  }

  private async initialiseUserList(payload: UserListResponse): Promise<void> {
    this.userList = [];
    this.backedUserList.clear();
    while (payload.users.length > 0) {
      const payloadChunk = payload.users.splice(0, USER_LIST_CHUNK_SIZE);
      const userListChunk = await this.initialiseUserListChunk(payloadChunk);
      this.addUsersToRoom(...userListChunk);
    }
    await this.sortByUserTypeThenAlphabeticallyByUsername(true);
  }

  private initialiseUserListChunk(payloadChunk: UserResponse[]): Promise<RoomUserModel[]> {
    return new Promise(async (resolve) => {
      const userListChunk: RoomUserModel[] = [];
      for (const user of payloadChunk) {
        const chatRoomUser = await this.createNewRoomUser(user);
        userListChunk.push(chatRoomUser);
      }
      window.setTimeout(() => resolve(userListChunk), USER_LIST_INITIALISATION_DELAY_MILLISECONDS);
    });
  }

  private async addUser(payload: AddUserResponse): Promise<void> {
    const chatRoomUser = await this.createNewRoomUser(payload);
    chatRoomUser.userChatStatus === UserChatStatus.ChatModerator || chatRoomUser.userChatStatus === UserChatStatus.TeamLeader || chatRoomUser.userChatStatus === UserChatStatus.ShiftLeader
      ? this.addChatmodsToRoom(chatRoomUser)
      : this.addUsersToRoom(chatRoomUser);

    this.notifyUserListChanged();
  }

  private removeUser(payload: RemoveUserResponse) {
    if (this.backedUserList.has(payload.username)) {
      const userIndex = this.userList.findIndex((user) => user.username === payload.username);
      this.backedUserList.delete(payload.username);
      this.userList.splice(userIndex, 1);
      this.notifyUserListChanged();
    }
  }

  private async modifyUser(payload: ModifyUserMessageResponse): Promise<void> {
    if (this.backedUserList.has(payload.username)) {
      const index = this.userList.findIndex((user) => user.username === payload.username);
      this.userList.splice(index, 1, await this.createNewRoomUser(payload));
      this.notifyUserListChanged();
    }
  }

  private async createNewRoomUser(user: UserResponse | AddUserResponse | ModifyUserMessageResponse): Promise<RoomUserModel> {
    if (!(await this.isIneligibleForPromoNameStylingChangeLive())){
      user.notEligibleForPromo = false;
    }
    const userChatStatus = await this.userChatStatusService.getChatStatusForUser(user.username);
    return new RoomUserModel(user, userChatStatus, this.gameName, this.chatRoomName, await this.isBirthdaysEnabled());
  }

  private async isIneligibleForPromoNameStylingChangeLive(): Promise<boolean> {
    const config = await this.configurationService.getConfiguration();
    return config.getConfigurationByPath(ConfigurationPaths.featuretoggle.ineligible_for_promo_name_styling_change_islive) === 'true';
  }

  private async isBirthdaysEnabled(): Promise<boolean> {
    const config = await this.configurationService.getConfiguration();
    const birthdaysEnabled = config.getConfigurationByPath(ConfigurationPaths.features.blings.birthday);
    return birthdaysEnabled === 'true' || birthdaysEnabled === undefined;
  }

  private addUsersToRoom(...users: RoomUserModel[]): void {
    users.forEach((user) => {
      if (!this.backedUserList.has(user.username)) {
        this.backedUserList.add(user.username);
        this.userList.push(user);
      }
    });
  }

  private addChatmodsToRoom(...users: RoomUserModel[]): void {
    users.forEach((user) => {
      if (!this.backedUserList.has(user.username)) {
        this.backedUserList.add(user.username);
        this.userList.unshift(user);
      }
    });
  }
}

export { RoomUserContainer };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/room-user-container/room-user-container.ts